import { useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import ProductIcon from '../icons/ProductIcon'
import { useModalFocus } from './modalFocus'

const cx = (...parts) => parts.filter(Boolean).join(' ')

const STATE_TONES = Object.freeze({
  proposed: 'proposal',
  pending_confirmation: 'proposal',
  awaiting_confirmation: 'proposal',
  awaiting_approval: 'proposal',
  approved: 'execution',
  queued: 'execution',
  running: 'execution',
  executing: 'execution',
  pending_async: 'execution',
  completed: 'execution',
  succeeded: 'execution',
  verified: 'verified',
  verification_passed: 'verified',
  failed: 'critical',
  error: 'critical',
  critical: 'critical',
  stale: 'warning',
  warning: 'warning',
  blind_spot: 'warning',
  unavailable: 'unavailable',
  active: 'verified',
  expired: 'warning',
  revoked: 'neutral',
  unknown: 'neutral',
  declined: 'neutral',
  cancelled: 'neutral',
})

export function stateTone(state) {
  return STATE_TONES[String(state || '').toLowerCase()] || 'neutral'
}

export function OpsButton({
  ref,
  tone = 'secondary',
  size = 'md',
  busy = false,
  busyLabel = 'Working',
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx('ops-button', `ops-button--${tone}`, `ops-button--${size}`, className)}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy && <span className="ops-button__spinner" aria-hidden="true" />}
      <span className="ops-button__label">{busy ? busyLabel : children}</span>
    </button>
  )
}

export function OpsSurface({ as: Component = 'section', level = 'base', className, children, ...rest }) {
  return (
    <Component className={cx('ops-surface', `ops-surface--${level}`, className)} {...rest}>
      {children}
    </Component>
  )
}

// A raw state reads in sentence case ("pending confirmation" -> "Pending confirmation");
// a label the caller passes is shown as written.
function sentenceCase(text) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function StateLabel({ state, label, className, ...rest }) {
  const tone = stateTone(state)
  return (
    <span className={cx('ops-state', `ops-state--${tone}`, className)} data-state={state || 'unknown'} {...rest}>
      <span className="ops-state__mark" aria-hidden="true" />
      {label || sentenceCase(String(state || 'unknown').replaceAll('_', ' '))}
    </span>
  )
}

export function OpsDisclosure({ summary, meta, open, className, children, ...rest }) {
  return (
    <details className={cx('ops-disclosure', className)} open={open} {...rest}>
      <summary>
        <span>{summary}</span>
        {meta && <span className="ops-disclosure__meta">{meta}</span>}
      </summary>
      <div className="ops-disclosure__body">{children}</div>
    </details>
  )
}

export function WorkHeader({ eyebrow, title, description, state, stateLabel, provenance, actions, className }) {
  return (
    <header className={cx('ops-work-header', className)}>
      <div className="ops-work-header__copy">
        {eyebrow && <p className="ops-kicker">{eyebrow}</p>}
        <div className="ops-work-header__title-row">
          <h1>{title}</h1>
          {state && <StateLabel state={state} label={stateLabel} />}
        </div>
        {description && <p className="ops-work-header__description">{description}</p>}
        {provenance && <p className="ops-provenance">{provenance}</p>}
      </div>
      {actions && <div className="ops-work-header__actions">{actions}</div>}
    </header>
  )
}

export function Proofline({ stages, className, ariaLabel = 'Work progress' }) {
  return (
    <ol className={cx('ops-proofline', className)} aria-label={ariaLabel}>
      {stages.map((stage, index) => (
        <li key={stage.id || stage.label} className={`ops-proofline__stage ops-proofline__stage--${stateTone(stage.state)}`}>
          <span className="ops-proofline__index" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
          <span className="ops-proofline__copy">
            <small>{stage.label}</small>
            <strong>{stage.detail}</strong>
          </span>
        </li>
      ))}
    </ol>
  )
}

export function EvidenceRecord({ title, source, observedAt, state = 'unknown', summary, children, className }) {
  return (
    <article className={cx('ops-evidence-record', className)}>
      <header>
        <div>
          <p className="ops-kicker">{source || 'Evidence source unavailable'}</p>
          <h3>{title}</h3>
        </div>
        <StateLabel state={state} />
      </header>
      {summary && <p className="ops-evidence-record__summary">{summary}</p>}
      {children}
      <footer className="ops-provenance">
        {observedAt ? `Observed ${observedAt}` : 'Observation time unavailable'}
      </footer>
    </article>
  )
}

export function EvidenceRail({ children, className, label = 'Evidence record' }) {
  return <div className={cx('ops-evidence-rail', className)} aria-label={label}>{children}</div>
}

export function ExecutionAttempt({ title, status, startedAt, completedAt, summary, children, className }) {
  return (
    <OpsSurface as="article" level="inset" className={cx('ops-execution-attempt', className)}>
      <header>
        <div>
          <p className="ops-kicker">Execution attempt</p>
          <h3>{title}</h3>
        </div>
        <StateLabel state={status} />
      </header>
      {summary && <p>{summary}</p>}
      {children}
      <footer className="ops-provenance">
        {startedAt ? `Started ${startedAt}` : 'Start time unavailable'}
        {completedAt ? ` · Completed ${completedAt}` : ''}
      </footer>
    </OpsSurface>
  )
}

export function VerificationGate({ verified, title = 'Independent verification', evidence, unavailableReason, className }) {
  const state = verified ? 'verified' : (unavailableReason ? 'unavailable' : 'completed')
  return (
    <OpsSurface as="section" level="inset" className={cx('ops-verification-gate', className)}>
      <header>
        <div>
          <p className="ops-kicker">Outcome gate</p>
          <h3>{title}</h3>
        </div>
        <StateLabel state={state} label={verified ? 'Verified' : unavailableReason ? 'Unavailable' : 'Not verified'} />
      </header>
      <p>{verified ? evidence : unavailableReason || 'Execution completion is retained, but no independent outcome proof is available.'}</p>
    </OpsSurface>
  )
}

const REQUIRED_DECISION_FIELDS = [
  ['scope', 'Exact scope'],
  ['effect', 'Expected effect'],
  ['artifact', 'Execution artifact'],
  ['risk', 'Evaluated risk'],
  ['preconditions', 'Preconditions'],
  ['stopCondition', 'Stop condition'],
  ['recovery', 'Recovery path'],
  ['verification', 'Verification plan'],
  ['authority', 'Named authority'],
]

export function decisionReadiness(dossier = {}) {
  const missing = REQUIRED_DECISION_FIELDS
    .filter(([key]) => !dossier[key] || (Array.isArray(dossier[key]) && dossier[key].length === 0))
    .map(([, label]) => label)
  return { ready: missing.length === 0, missing }
}

function DossierValue({ label, value }) {
  return (
    <div className="ops-dossier__field">
      <dt>{label}</dt>
      <dd>{Array.isArray(value) ? value.join(', ') : value || 'Unavailable'}</dd>
    </div>
  )
}

export function ChangeDossier({ dossier, className }) {
  return (
    <dl className={cx('ops-dossier', className)}>
      {REQUIRED_DECISION_FIELDS.map(([key, label]) => (
        <DossierValue key={key} label={label} value={dossier?.[key]} />
      ))}
    </dl>
  )
}

export function DecisionSurface({
  title,
  description,
  dossier,
  canApprove,
  approving,
  onApprove,
  onDecline,
  approveLabel = 'Approve exact change',
  className,
}) {
  const readiness = decisionReadiness(dossier)
  const enabled = readiness.ready && canApprove && typeof onApprove === 'function' && !approving
  const blockedReason = !readiness.ready
    ? `Approval blocked. Missing: ${readiness.missing.join(', ')}.`
    : !canApprove
      ? 'Approval blocked. Named authority is not available for this operator.'
      : typeof onApprove !== 'function'
        ? 'Approval blocked. The decision action is unavailable.'
        : null

  return (
    <OpsSurface as="section" level="raised" className={cx('ops-decision-surface', className)}>
      <header>
        <div>
          <p className="ops-kicker">Authority decision</p>
          <h2>{title}</h2>
          {description && <p>{description}</p>}
        </div>
        <StateLabel state="awaiting_approval" label="Awaiting authority" />
      </header>
      <ChangeDossier dossier={dossier} />
      {blockedReason && <p className="ops-decision-surface__blocked" role="status">{blockedReason}</p>}
      <footer>
        {onDecline && <OpsButton tone="secondary" onClick={onDecline}>Decline</OpsButton>}
        <OpsButton tone="primary" busy={approving} busyLabel="Approving" disabled={!enabled} onClick={onApprove}>
          {approveLabel}
        </OpsButton>
      </footer>
    </OpsSurface>
  )
}

export function LiveUpdateGate({ count, onApply, className }) {
  if (!count) return null
  return (
    <div className={cx('ops-live-update', className)} role="status">
      <span>{count} new {count === 1 ? 'record is' : 'records are'} ready to review.</span>
      <OpsButton size="sm" onClick={onApply}>Show updates</OpsButton>
    </div>
  )
}

const capitalize = (text) => (text ? text[0].toUpperCase() + text.slice(1) : text)

// Default copy per kind, written from the region's noun ("hosts", "scan targets").
// An explicit title or detail always wins, so pinned copy stays as it is.
function defaultCopy({ kind, noun, reason, permission, integration }) {
  const thing = noun || 'this'
  const service = integration || 'This integration'
  switch (kind) {
    case 'loading': return { title: noun ? `Loading ${noun}…` : 'Loading…' }
    case 'empty': return { title: noun ? `No ${noun} yet` : 'Nothing here yet' }
    case 'filtered': return { title: noun ? `No ${noun} match these filters` : 'Nothing matches these filters' }
    case 'error': return { title: noun ? `Couldn't load ${noun}` : "Couldn't load this" }
    case 'stale': return { title: noun ? `Showing stored ${noun}` : 'Showing stored data' }
    case 'unavailable':
      if (reason === 'not-configured') return { title: `${service} isn't set up` }
      if (reason === 'offline') return { title: `${service} isn't responding` }
      if (reason === 'off') return { title: `${capitalize(thing)} is turned off` }
      return { title: `${capitalize(thing)} isn't available` }
    case 'denied':
      return {
        title: `You don't have access to ${thing}`,
        detail: permission ? `Ask an administrator for ${permission}.` : undefined,
      }
    default: return {}
  }
}

// Each kind also says its state in words, never by colour alone.
const STATE_MARKS = { error: 'Error', stale: 'Stale', unavailable: 'Unavailable', denied: 'No access' }

/* One state per region. kind: loading | empty | filtered | error | stale | unavailable | denied
   (notice and permission remain for existing callers). A stale age goes in `detail`. */
export function ViewState({
  kind,
  noun,
  title,
  detail,
  action,
  reason,
  permission,
  integration,
  onRetry,
  onClearFilters,
  className,
}) {
  const copy = defaultCopy({ kind, noun, reason, permission, integration })
  const heading = title ?? copy.title
  const body = detail ?? copy.detail
  const mark = STATE_MARKS[kind]
  const retry = onRetry && (kind === 'error' || (kind === 'unavailable' && reason === 'offline'))
  return (
    <div className={cx('ops-view-state', `ops-view-state--${kind}`, className)} role={kind === 'error' ? 'alert' : 'status'}>
      {kind === 'loading' && <span className="ops-view-state__spinner" aria-hidden="true" />}
      {kind === 'denied' && <ProductIcon name="ui/lock-keyhole" size={16} className="ops-view-state__icon" />}
      <div>
        {mark && <span className="ops-view-state__mark">{mark}</span>}
        <strong>{heading}</strong>
        {body && <p>{body}</p>}
      </div>
      {kind === 'filtered' && onClearFilters && (
        <OpsButton tone="quiet" size="sm" onClick={onClearFilters}>Clear filters</OpsButton>
      )}
      {retry && <OpsButton size="sm" onClick={onRetry}>Try again</OpsButton>}
      {action}
    </div>
  )
}

/* Picks exactly one state for a region from the flags a page already has; first match wins.
   busy: a request is running while data stays on screen, so the region keeps its content
   and shows a spinner by its heading. */
export function resolveRegionState({
  loading = false,
  refreshing = false,
  error = null,
  hasData = false,
  count,
  filtered = false,
  stale = false,
  denied = false,
  unavailable = false,
} = {}) {
  const message = error ? (typeof error === 'string' ? error : error.message) || undefined : undefined
  const busy = Boolean(hasData && (loading || refreshing))
  const result = (kind, extra) => ({ kind, busy, ...extra })
  if (denied) return result('denied')
  if (unavailable) return result('unavailable')
  if (error && !hasData) return result('error', message ? { detail: message } : undefined)
  if (hasData && (stale || error)) return result('stale', message ? { detail: message } : undefined)
  if (loading && !hasData) return result('loading')
  const size = count ?? (hasData ? undefined : 0)
  if (size === 0 && filtered) return result('filtered')
  if (size === 0) return result('empty')
  return result('ready')
}

/* `header` replaces the default title row and Close button (for example a search field with
   its own Cancel); `title` then names the dialog for assistive technology. While a Guide
   tour panel is on the page, Tab cycles through the sheet and the panel together. */
export function OpsSheet({ open, title, onClose, children, returnFocusRef, className, anchor = 'end', header }) {
  const titleId = useId()
  const panelRef = useRef(null)
  useModalFocus({ open, panelRef, onClose, returnFocusRef })

  if (!open) return null
  // Page animation transforms create a containing block for fixed children.
  // Mount modal sheets at the body so their bounds remain the viewport.
  return createPortal(
    <div className={cx('ops-sheet', anchor === 'start' && 'ops-sheet--start')} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
      <section
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={header ? undefined : titleId}
        aria-label={header ? title : undefined}
        tabIndex={-1}
        className={cx('ops-sheet__panel', className)}
      >
        {header ? <header>{header}</header> : (
          <header>
            <h2 id={titleId}>{title}</h2>
            <OpsButton tone="quiet" aria-label="Close" onClick={onClose}>Close</OpsButton>
          </header>
        )}
        <div className="ops-sheet__body">{children}</div>
      </section>
    </div>, document.body,
  )
}
